import {
  FEATURE_SCOPES,
  SCOPES,
  useHasFeatureScope,
  useHasScope,
} from '@data/auth';
import { Box, Table, TableBody, Typography, styled } from '@mui/material';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { isEqual } from 'lodash';
import type { HTMLAttributes } from 'react';
import React, { useRef, useMemo } from 'react';
import { useMount, useUnmount } from 'react-use';
import { useTranslation } from 'react-i18next';
import type { MediaClient, RemoteCamera } from '@tier4/webauto-media-client';
import RemoteCallButton from '../RemoteCallButton';

type CameraProps = HTMLAttributes<HTMLDivElement> & {
  camera: RemoteCamera;
  component?: 'td' | 'div';
  children?: React.ReactNode;
};

const Camera: React.FC<CameraProps> = ({
  camera,
  component = 'div',
  children,
}: CameraProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  /**
   * マウント時
   * カメラに接続しVideoに反映
   */
  useMount(async () => {
    if (isNullOrUndefined(camera)) return;
    const stream: MediaStream = await camera.connect();
    if (!isNullOrUndefined(videoRef.current)) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch((error) => console.log(error));
    }
  });

  /**
   * アンマウント時
   */
  useUnmount(async () => {
    if (!isNullOrUndefined(videoRef.current)) {
      const stream = videoRef.current.srcObject as MediaStream;
      if (isNullOrUndefined(stream)) return;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    if (!isNullOrUndefined(camera)) {
      await camera.disconnect();
    }
  });

  if (isNullOrUndefined(camera)) return null;

  if (component === 'td') {
    return (
      <CameraWrapperTd className={camera.tag}>
        <Video ref={videoRef} />
        <Tag>{camera.tag}</Tag>
        {children}
      </CameraWrapperTd>
    );
  }

  return (
    <CameraWrapperDiv className={camera.tag}>
      <Video ref={videoRef} />
      <Tag>{camera.tag}</Tag>
      {children}
    </CameraWrapperDiv>
  );
};

const CameraWrapperDiv = styled('div')`
  position: absolute;
  box-sizing: border-box;
  background-color: black;
  width: 100%;
  height: 100%;
`;

const CameraWrapperTd = styled('td')`
  position: relative;
  box-sizing: border-box;
`;

const Video = styled('video')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: black;
`;

const Tag = styled('p')`
  color: black;
  position: absolute;
  text-transform: capitalize;
  margin: 0;
  line-height: 1;
  padding: 4px;
  user-select: none;
  background-color: rgba(255, 255, 255, 0.5);

  .front-center & {
    bottom: 8px;
    left: calc(50% - 42px);
  }

  .front-left & {
    top: 8px;
    right: 8px;
  }

  .front-right & {
    top: 8px;
    left: 8px;
  }

  .left-mirror & {
    top: 8px;
    right: 8px;
  }

  .right-mirror & {
    top: 8px;
    left: 8px;
  }

  .rear-mirror & {
    top: 8px;
    left: calc(50% - 39px);
  }

  .cabin &,
  .panel & {
    top: 8px;
  }

  .cabin & {
    .camera7 & {
      left: calc(50% - 21px);
    }
  }

  .cabin & {
    .camera8 & {
      right: 8px;
    }
  }

  .panel & {
    .camera8 & {
      left: 8px;
    }
  }

  td & {
    top: 8px !important;
    left: 8px !important;
    bottom: auto !important;
    right: auto !important;
  }
`;

/**
 * カメラの数によってTableの行と列の数を返す
 * @param {Number} cameraNum
 * @return {Object}
 */
const getTableSet = (cameraNum: number) => {
  if (cameraNum === 2) {
    return {
      row: 1,
      column: 2,
    };
  }

  if (cameraNum >= 3 && cameraNum <= 4) {
    return {
      row: 2,
      column: 2,
    };
  }

  if (cameraNum >= 5 && cameraNum <= 6) {
    return {
      row: 2,
      column: 3,
    };
  }

  if (cameraNum >= 7 && cameraNum <= 8) {
    return {
      row: 2,
      column: 4,
    };
  }

  return {
    row: 3,
    column: 4,
  };
};

enum EightCamerasPredefinedTags {
  FrontCenter = 'front-center',
  FrontLeft = 'front-left',
  FrontRight = 'front-right',
  RearMirror = 'rear-mirror',
  LeftMirror = 'left-mirror',
  RightMirror = 'right-mirror',
  Panel = 'panel',
  Cabin = 'cabin',
}

type Props = {
  devices: MediaClient.RemoteDevices | undefined;
};

const RemoteMedia: React.FC<Props> = ({ devices }: Props) => {
  // const remoteMonitorConfig = useAtomValue(remoteMonitorConfigAtom);
  const getHasFeatureScope = useHasFeatureScope();
  const getHasScope = useHasScope();
  const { t } = useTranslation();

  /**
   * 遠隔通話機能 & 権限があるか
   */
  const hasCallScope = useMemo(
    () =>
      getHasFeatureScope(FEATURE_SCOPES.VoiceCall) && getHasScope(SCOPES.Call),
    [getHasFeatureScope, getHasScope],
  );

  /**
   * 遠隔カメラ機能 & 権限があるか
   */
  const hasCameraScope = useMemo(
    () =>
      getHasFeatureScope(FEATURE_SCOPES.VideoStreaming) &&
      getHasScope([SCOPES.DescribeMediaStatus, SCOPES.ReceiveMediaStream]),
    [getHasFeatureScope, getHasScope],
  );

  /**
   * カメラのタグ一覧
   */
  const tags = useMemo(
    () => (devices ? devices.cameras.map((camera) => camera.tag) : []),
    [devices],
  );

  const hasSevenCameras = useMemo(() => {
    const filteredTags = Object.values(EightCamerasPredefinedTags).filter(
      (tag) => tag !== 'panel',
    );
    return isEqual(tags.sort(), filteredTags.sort());
  }, [tags]);

  const hasEightCameras = useMemo(
    () =>
      isEqual(tags.sort(), Object.values(EightCamerasPredefinedTags).sort()),
    [tags],
  );

  /**
   * カメラ配置
   */
  const content = useMemo(() => {
    if (!devices || devices.cameras.length === 0 || !hasCameraScope) {
      return (
        <Wrapper data-testid="media-view">
          <Box pt={6}>
            <Typography
              align="center"
              sx={{ color: 'white' }}
              data-testid="camera-caption"
            >
              {t(
                `remote.cameras.${!hasCameraScope ? 'no_scope' : 'no_camera'}`,
              )}
            </Typography>
          </Box>
          {hasCallScope && devices && (
            <RemoteCallButton telephones={devices.telephones} />
          )}
        </Wrapper>
      );
    }

    if (devices.cameras.length === 1) {
      return (
        <Wrapper>
          <Camera camera={devices.cameras[0]} />
          {hasCallScope && devices && (
            <RemoteCallButton telephones={devices?.telephones} />
          )}
        </Wrapper>
      );
    }

    const { row, column } = getTableSet(devices.cameras.length);
    let n = 0;

    return (
      <Wrapper>
        <Table sx={{ height: '100%' }}>
          <TableBody>
            {[...Array(row).keys()].map((i) => (
              <tr key={`row${i}`}>
                {[...Array(column).keys()].map(() => {
                  const camera = devices?.cameras[n];
                  n += 1;
                  return (
                    <Camera key={`camera${n}`} camera={camera} component="td" />
                  );
                })}
              </tr>
            ))}
          </TableBody>
        </Table>
        {hasCallScope && devices && (
          <RemoteCallButton telephones={devices?.telephones} />
        )}
      </Wrapper>
    );
  }, [devices, hasCameraScope, t, hasCallScope]);

  if (hasSevenCameras || hasEightCameras) {
    return (
      <Wrapper>
        <CamerasWrapper className={`camera${devices?.cameras.length}`}>
          {devices?.cameras.map((camera) => {
            if (camera.tag.toLowerCase() === 'cabin') {
              return (
                <Camera key={camera.id} camera={camera}>
                  {hasCallScope && (
                    <RemoteCallButton telephones={devices.telephones} />
                  )}
                </Camera>
              );
            }
            return <Camera key={camera.id} camera={camera} />;
          })}
          <VehicleInfo>
            {hasCallScope && devices && (
              <RemoteCallButton telephones={devices.telephones} />
            )}
          </VehicleInfo>
        </CamerasWrapper>
      </Wrapper>
    );
  }

  return content;
};

export default React.memo(RemoteMedia);

const Wrapper = styled(Box)`
  width: 100%;
  height: 100%;
  position: relative;
`;

const CamerasWrapper = styled(Box)`
  position: relative;
  height: 100%;
  width: 100%;

  .front-center {
    width: 100%;
    height: 25%;
    top: 0;
    left: 0;
  }

  .front-left,
  .front-right {
    width: calc(50% - 80px);
    height: 25%;
    top: 25%;
  }

  .front-left {
    left: 0;
  }

  .front-right {
    right: 0;
  }

  .left-mirror,
  .right-mirror,
  .rear-mirror {
    top: 50%;
    width: 33.333333%;
    height: 20%;
  }

  .left-mirror {
    left: 0;
  }

  .rear-mirror {
    left: 33.3333333%;
  }

  .right-mirror {
    right: 0;
  }

  &.camera7,
  &.camera8 {
    .panel,
    .cabin {
      bottom: 0;
      height: 30%;
    }

    .cabin {
      left: 0;
    }
  }

  &.camera7 {
    .cabin {
      width: 100%;
    }
  }

  &.camera8 {
    .cabin,
    .panel {
      width: 50%;
    }

    .panel {
      right: 0;
    }
  }
`;

const VehicleInfo = styled(Box)`
  position: absolute;
  width: 160px;
  height: 25%;
  top: 25%;
  left: calc(50% - 80px);
  background-color: rgba(255, 255, 255, 0.3);
  background-image: url(/assets/img/maps/remote_vehicle_image.png);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;
